import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export class Tabs {
  constructor() {
    this.isInitialized = false;
    this.tabs = new Map();
  }

  init() {
    if (this.isInitialized) return;

    this.initTabs();
    this.isInitialized = true;
  }

  initTabs() {
    document.querySelectorAll('.tab_wrap').forEach((container, index) => {
      if (this.tabs.has(container)) {
        this.destroyTab(container);
      }

      const tab = new TabInstance(container, index);
      this.tabs.set(container, tab);
    });
  }

  destroyTab(container) {
    const tab = this.tabs.get(container);
    if (tab) {
      tab.destroy();
      this.tabs.delete(container);
    }
  }

  destroy() {
    this.tabs.forEach((tab, container) => {
      this.destroyTab(container);
    });

    this.isInitialized = false;
  }
}

class TabInstance {
  constructor(container, index) {
    this.container = container;
    this.index = index;
    this.activeIndex = 0;
    this.animating = false;
    this.canPlay = true;
    this.autoplayTl = null;
    this.timelines = [];
    this.eventListeners = [];

    // Get configuration from data attributes
    this.loopControls = container.getAttribute('data-loop-controls') === 'true';
    this.slideTabs = container.getAttribute('data-slide-tabs') === 'true';
    this.pauseOnHover = container.getAttribute('data-pause-on-hover') === 'true';
    this.autoplay = Number(container.getAttribute('data-autoplay-duration')) || 0;
    this.duration = Number(container.getAttribute('data-duration')) || 0.3;

    // Get DOM elements
    this.buttonList = container.querySelector('.tab_button_list');
    this.panelList = container.querySelector('.tab_content_list');
    this.previousButton = container.querySelector('[data-tab="previous"] button');
    this.nextButton = container.querySelector('[data-tab="next"] button');
    this.toggleWrap = container.querySelector('[data-tab-button="toggle"]');
    this.toggleButton = container.querySelector('[data-tab-button="toggle"] button');

    this.init();
  }

  init() {
    if (this.container.dataset.scriptInitialized) return;
    this.container.dataset.scriptInitialized = 'true';

    this.removeCMSList(this.buttonList);
    this.removeCMSList(this.panelList);

    this.buttonItems = Array.from(this.buttonList.children);
    this.panelItems = Array.from(this.panelList.children);

    if (!this.buttonList || !this.panelList || !this.buttonItems.length || !this.panelItems.length) {
      console.warn('Missing elements in:', this.container);
      return;
    }

    this.setupAccessibility();
    this.setupTabs();
    this.makeActive(0, false, false);
    this.setupNavigation();
    this.setupAutoplay();
  }

  removeCMSList(slot) {
    if (!slot) return;

    const dynList = Array.from(slot.children).find((child) =>
      child.classList.contains('w-dyn-list')
    );

    if (!dynList) return;

    const nestedItems = dynList?.firstElementChild?.children;
    if (!nestedItems) return;

    const staticWrapper = [...slot.children];
    [...nestedItems].forEach(el => {
      if (el.firstElementChild) {
        slot.appendChild(el.firstElementChild);
      }
    });

    staticWrapper.forEach((el) => el.remove());
  }

  setupAccessibility() {
    this.panelItems.forEach((panel) => {
      panel.style.display = 'none';
      panel.setAttribute('role', 'tabpanel');
    });

    this.panelList.removeAttribute('role');
    this.buttonList.setAttribute('role', 'tablist');

    this.buttonItems.forEach((btn) => {
      btn.setAttribute('role', 'tab');
    });
  }

  setupTabs() {
    this.buttonItems.forEach((btn, index) => {
      let tabId = this.container.getAttribute('data-tab-component-id');
      tabId = tabId ? tabId.toLowerCase().replaceAll(' ', '-') : this.index + 1;
      let itemId = btn.getAttribute('data-tab-item-id');
      itemId = itemId ? itemId.toLowerCase().replaceAll(' ', '-') : index + 1;

      btn.setAttribute('id', `tab-button-${tabId}-${itemId}`);
      btn.setAttribute('aria-controls', `tab-panel-${tabId}-${itemId}`);
      this.panelItems[index]?.setAttribute('id', `tab-panel-${tabId}-${itemId}`);
      this.panelItems[index]?.setAttribute('aria-labelledby', btn.id);

      // Check URL parameter
      if (new URLSearchParams(location.search).get('tab-id') === `${tabId}-${itemId}`) {
        this.makeActive(index);
        this.autoplay = 0;
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState({}, '', ((u) => (u.searchParams.delete('tab-id'), u))(new URL(location.href)));
      }

      // Add event listeners
      const clickHandler = () => this.makeActive(index);
      const keydownHandler = (e) => {
        if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
          this.updateIndex(1, true);
        } else if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
          this.updateIndex(-1, true);
        }
      };

      btn.addEventListener('click', clickHandler);
      btn.addEventListener('keydown', keydownHandler);

      this.eventListeners.push(
        { element: btn, type: 'click', handler: clickHandler },
        { element: btn, type: 'keydown', handler: keydownHandler }
      );
    });
  }

  setupNavigation() {
    if (this.nextButton) {
      const nextHandler = () => this.updateIndex(1);
      this.nextButton.addEventListener('click', nextHandler);
      this.eventListeners.push({ element: this.nextButton, type: 'click', handler: nextHandler });
    }

    if (this.previousButton) {
      const previousHandler = () => this.updateIndex(-1);
      this.previousButton.addEventListener('click', previousHandler);
      this.eventListeners.push({ element: this.previousButton, type: 'click', handler: previousHandler });
    }
  }

  setupAutoplay() {
    if (this.autoplay === 0 || typeof gsap === 'undefined') return;

    this.autoplayTl = gsap.timeline({ repeat: -1 }).fromTo(
      this.container,
      { '--progress': 0 },
      {
        onComplete: () => this.updateIndex(1, false, false),
        '--progress': 1,
        ease: 'none',
        duration: this.autoplay
      }
    );

    let isHovered = false;
    let hasFocusInside = false;
    let prefersReducedMotion = false;
    let inView = true;

    const updateAuto = () => {
      if (prefersReducedMotion || !inView || this.canPlay || isHovered || hasFocusInside) {
        this.autoplayTl.pause();
      } else {
        this.autoplayTl.play();
      }
    };

    const setButton = () => {
      this.canPlay = !this.canPlay;
      this.toggleButton?.setAttribute('aria-pressed', !this.canPlay ? 'true' : 'false');
      this.toggleWrap?.classList.toggle('is-pressed', !this.canPlay);
      if (!this.canPlay) {
        isHovered = hasFocusInside = prefersReducedMotion = false;
      }
      updateAuto();
    };

    setButton();

    if (this.toggleButton) {
      const toggleHandler = () => setButton();
      this.toggleButton.addEventListener('click', toggleHandler);
      this.eventListeners.push({ element: this.toggleButton, type: 'click', handler: toggleHandler });
    }

    const handleMotionChange = (e) => {
      prefersReducedMotion = e.matches;
      updateAuto();
      this.canPlay = !e.matches;
      setButton();
    };

    const motionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    handleMotionChange(motionMediaQuery);
    motionMediaQuery.addEventListener('change', handleMotionChange);

    if (this.pauseOnHover) {
      const mouseenterHandler = () => {
        isHovered = true;
        updateAuto();
      };
      const mouseleaveHandler = () => {
        hasFocusInside = false;
        isHovered = false;
        updateAuto();
      };

      this.container.addEventListener('mouseenter', mouseenterHandler);
      this.container.addEventListener('mouseleave', mouseleaveHandler);

      this.eventListeners.push(
        { element: this.container, type: 'mouseenter', handler: mouseenterHandler },
        { element: this.container, type: 'mouseleave', handler: mouseleaveHandler }
      );
    }

    const focusinHandler = () => {
      hasFocusInside = true;
      updateAuto();
    };
    const focusoutHandler = (e) => {
      if (!e.relatedTarget || !this.container.contains(e.relatedTarget)) {
        hasFocusInside = false;
        updateAuto();
      }
    };

    this.container.addEventListener('focusin', focusinHandler);
    this.container.addEventListener('focusout', focusoutHandler);

    this.eventListeners.push(
      { element: this.container, type: 'focusin', handler: focusinHandler },
      { element: this.container, type: 'focusout', handler: focusoutHandler }
    );

    // Intersection Observer
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        inView = entries[0].isIntersecting;
        updateAuto();
      },
      { threshold: 0 }
    );
    this.intersectionObserver.observe(this.container);
  }

  makeActive(index, focus = false, animate = true, pause = true) {
    if (this.animating) return;

    this.buttonItems.forEach((btn, i) => {
      btn.classList.toggle('is-active', i === index);
      btn.setAttribute('aria-selected', i === index ? 'true' : 'false');
      btn.setAttribute('tabindex', i === index ? '0' : '-1');
    });

    this.panelItems.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
    });

    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }

    if (this.nextButton) {
      this.nextButton.disabled = index === this.buttonItems.length - 1 && !this.loopControls;
    }

    if (this.previousButton) {
      this.previousButton.disabled = index === 0 && !this.loopControls;
    }

    if (focus) {
      this.buttonItems[index].focus();
    }

    const previousPanel = this.panelItems[this.activeIndex];
    const currentPanel = this.panelItems[index];
    let direction = this.activeIndex > index ? -1 : 1;

    if (typeof gsap !== 'undefined' && animate && this.activeIndex !== index) {
      if (this.autoplayTl && !this.canPlay && typeof this.autoplayTl.restart === 'function') {
        this.autoplayTl.restart();
      }

      this.animating = true;

      const tl = gsap.timeline({
        onComplete: () => (this.animating = false),
        defaults: { duration: this.duration, ease: 'power1.out' }
      });

      this.timelines.push(tl);

      if (this.slideTabs) {
        tl.set(currentPanel, { display: 'block', position: 'relative' });
        if (previousPanel) {
          tl.set(previousPanel, { position: 'absolute', top: 0, left: 0, width: '100%' });
          tl.fromTo(previousPanel, { xPercent: 0 }, { xPercent: -120 * direction });
        }
        tl.fromTo(currentPanel, { xPercent: 120 * direction }, { xPercent: 0 }, '<');
        if (previousPanel) {
          tl.set(previousPanel, { display: 'none' });
        }
      } else {
        if (previousPanel) {
          tl.to(previousPanel, { opacity: 0 });
          tl.set(previousPanel, { display: 'none' });
        }
        tl.set(currentPanel, { display: 'block' });
        tl.fromTo(currentPanel, { opacity: 0 }, { opacity: 1 });
      }
    } else {
      if (previousPanel) previousPanel.style.display = 'none';
      if (currentPanel) currentPanel.style.display = 'block';
    }

    this.buttonList.scrollTo({
      left: this.buttonItems[index].offsetLeft - this.buttonList.clientWidth / 2,
      behavior: 'smooth'
    });

    this.activeIndex = index;
  }

  updateIndex(delta, focus = false, pause = true) {
    const newIndex = (this.activeIndex + delta + this.buttonItems.length) % this.buttonItems.length;
    this.makeActive(newIndex, focus, true, pause);
  }

  destroy() {
    // Kill autoplay timeline
    if (this.autoplayTl) {
      this.autoplayTl.kill();
      this.autoplayTl = null;
    }

    // Kill all animation timelines
    this.timelines.forEach(tl => {
      if (tl) {
        tl.kill();
      }
    });
    this.timelines = [];

    // Remove all event listeners
    this.eventListeners.forEach(({ element, type, handler }) => {
      element.removeEventListener(type, handler);
    });
    this.eventListeners = [];

    // Disconnect intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    // Clear dataset flag
    delete this.container.dataset.scriptInitialized;
  }
}